import { Monitor, Presentation, DoorClosed } from 'lucide-react';

/**
 * The three space types — the single definition of what a category means in this app.
 *
 * This module holds the taxonomy and nothing else. It was once the innards of a header filter
 * component; the filter is gone, the taxonomy stayed. Keeping it in its own module is the point:
 * the CategoryBar plan's finding B1 is about two places growing two different ideas of the same
 * word, and that only stays fixed while there is exactly one place to change.
 *
 * After the model of explore.upflex.com.
 *
 * Upflex offers exactly three — Desks, Meeting Rooms, Private Offices — as toggles, and repeats
 * the same three words on every listing card ("38 Desks, 3 Meeting Rooms, 24 Private Offices").
 * The bar here used to carry nine, and they were not nine decisions: "Open", "Flex" and "Cowork"
 * described the same room, "Fix" versus "Flex" is a booking model rather than a room, and the
 * labels had to be clipped to word fragments to fit nine of them across a phone.
 *
 * Measured against production on 21.09.2026: Arbeitsplatz 68, Meetingraum 36, Büro 13. Each one
 * clears the SEO plan's thin-content threshold of 8 with room to spare, which the nine did not —
 * three of them sat below it.
 *
 * Which booking model applies (day pass, monthly fixdesk, hourly) is the order form's job, not
 * this filter's. Mixing the two dimensions is the defect this replaces.
 *
 * ⚠️ Never hardcode a category ID. The IDs live in the hosted `listing-categories.json`; these
 * matchers resolve against whatever is configured there, which is why the filter survives a
 * renamed category in the Console. A category matching no group is dropped — that is how
 * "Coliving" once ended up next to "Meeting".
 */
const GROUPS = [
  {
    key: 'desk',
    match: (id, name) =>
      /open.?space|hot.?desk|fix(ed)?.?desk|community|desk/i.test(id) ||
      /open.?space|hot.?desk|fix(ed)?.?desk|community|coworking|flex/i.test(name),
    Icon: Monitor,
    labelId: 'CategoryShortcuts.desk',
  },
  {
    key: 'meeting',
    match: (id, name) => /meeting|konferenz/i.test(id) || /meeting|konferenz/i.test(name),
    Icon: Presentation,
    labelId: 'CategoryShortcuts.meeting',
  },
  {
    // `virtual_office` matches /office/ and is excluded on purpose. It is a business address with
    // a mail service, not a room — Upflex lists only real rooms under "Private Offices", and
    // someone pressing "Büro" is looking for a door they can shut. It also keeps this group
    // identical to the office section of the city pages, which has always carried private and
    // team office and lists Virtual Office separately; two different office counts on one page
    // is exactly the drift finding B1 of the CategoryBar plan describes.
    key: 'office',
    match: (id, name) =>
      (/office|b[üu]ro/i.test(id) && !/virtual/i.test(id)) ||
      (/office|b[üu]ro/i.test(name) && !/virtual/i.test(name)),
    Icon: DoorClosed,
    labelId: 'CategoryShortcuts.office',
  },
];

/**
 * Fallback for when hosted categories are not configured or have not loaded yet. Mirrors the
 * marketplace's own ids so the links still resolve — every id a group claims has to be in here,
 * or the fallback path silently produces a different result set than the hosted one.
 */
export const FALLBACK_CATEGORIES = [
  { id: 'open_space', name: 'Open Space' },
  { id: 'hot_desk', name: 'Hot Desk' },
  { id: 'fixed_desk', name: 'Fixed Desk' },
  { id: 'community_space', name: 'Community Space' },
  { id: 'meeting_room', name: 'Meeting Room' },
  { id: 'private_office', name: 'Private Office' },
  { id: 'team_office', name: 'Team Office' },
];

/**
 * Resolve the hosted categories into the three groups, in the order of GROUPS rather than the
 * order of the hosted list — the filter then reads the same however the Console sorts.
 *
 * @param {Array} categories hosted categories ({ id, name })
 * @returns {Array} groups that matched at least one category, each with its `ids`
 */
export const resolveGroups = categories =>
  GROUPS.map(group => {
    const ids = (categories || [])
      .filter(c => group.match((c?.id || '').toLowerCase(), c?.name || ''))
      .map(c => c.id);
    return ids.length > 0 ? { ...group, ids } : null;
  }).filter(Boolean);

/**
 * The query string of a category search: the city bounds (raw, as every bounds link in the app
 * writes them) plus the group's hosted ids. One builder for the homepage shortcuts and the city
 * page pills, so both link to the same result set — two hand-built copies drifted apart once
 * before (finding B1 of the CategoryBar plan).
 *
 * @param {string} paramName the public category param, from constructQueryParamName
 * @param {string[]} ids hosted category ids; empty → no category filter
 * @param {string} [boundsStr] city bounds; omit for a Switzerland-wide search
 * @returns {string} `?bounds=…&pub_categoryLevel1=…`, or '' when there is nothing to filter by
 */
export const categorySearchString = (paramName, ids, boundsStr) => {
  const parts = [];
  if (boundsStr) {
    parts.push(`bounds=${boundsStr}`);
  }
  if (ids && ids.length > 0) {
    parts.push(`${paramName}=${encodeURIComponent(ids.join(','))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
};

/** The group keys in display order — the order the city page cycles through in its "all" view. */
export const GROUP_KEYS = GROUPS.map(group => group.key);

/**
 * Which group a listing's `publicData.categoryLevel1` belongs to, or null when it is none of the
 * three (virtual office, lounge, coliving, …). Matches on the id alone, so it works before the
 * hosted categories have loaded and does not depend on how the Console names things.
 *
 * @param {string} categoryId listing category id, e.g. `meeting_room`
 * @returns {'desk'|'meeting'|'office'|null}
 */
export const groupKeyForCategoryId = categoryId => {
  const id = (categoryId || '').toLowerCase();
  if (!id) {
    return null;
  }
  const group = GROUPS.find(g => g.match(id, ''));
  return group ? group.key : null;
};
